import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { getCollabChatRoom } from "services/api/chat";
import { fetchCollaborationMilestone, fetchCollaborationTasks } from "services/api/collaboration";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { CollaborationItem, CollaborationsSlice, MilestoneData } from "types/collaboration";
import { CollaborationChatRoom } from "types/collaboration/chat";
import { generateUniqueKey, getErrorMessage, isFresh } from "utils/helpers";

export const fetchAllCollaborationsThunk = createAsyncThunk<{count: number, items: CollaborationItem[], gig_types: any[], gig_steps: any[], filters: { page: number, [key: string]: any }, filterId: string}, {filters: { page: number, [key: string]: any }, force?: boolean}, {rejectValue: string}>(
    'collaborations',
    async({filters, force}, {getState, rejectWithValue}) => {
        try {
            const state = getState() as { auth: AuthState, collaborations: CollaborationsSlice };
            const filterId = generateUniqueKey(filters, ['page']);
            const STALE_TIME = apiConfig.FETCH_COLLABORATION_TASKS.staleTime;

            if(filters.page === 1 && !force && isFresh(state.collaborations?.filtered_lists?.[filterId]?.last_fetched_at, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const {count, items, gig_types, gig_steps} = await fetchCollaborationTasks({...filters, limit: 24});

            return {count, items, filters, filterId, gig_types, gig_steps}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch collaboration!"));
        }
    }
)

export const fetchCollaborationMilestoneData = createAsyncThunk<{data: MilestoneData, collaborationId: string, campaignId: string, milestoneId: string, cache?: boolean}, {campaignId: string, milestoneId: string, collaborationId: string, force?: boolean}, {rejectValue: string}>(
    'campaign/collaboration/milestone',
    async ({campaignId, collaborationId, milestoneId, force}, { getState, rejectWithValue }) => {
        try { 
            const STALE_TIME = apiConfig.FETCH_COLLABORATION_MILESTONE.staleTime;
            const state = getState() as {auth: AuthState, collaborations: CollaborationsSlice};
            const existingData = state.collaborations.milestones?.[campaignId]?.[collaborationId]?.[milestoneId] ?? null
            if(!force && isFresh(existingData?.last_fetched_at ?? null, STALE_TIME)){
                return {milestoneId, campaignId, collaborationId, data: existingData.milestone, cache: true}
            }
            const data = await fetchCollaborationMilestone(campaignId, collaborationId, milestoneId);
            return {milestoneId, campaignId, collaborationId, data};
        } catch (error) {
            return rejectWithValue('Failed to fetch collaboration milestone');
        }
    }
)

export const fetchCollaborationChatRoom = createAsyncThunk<{room: CollaborationChatRoom, collaborationId: string, campaignId: string, cache?: boolean}, {campaignId: string, collaborationId: string, force?: boolean}, {rejectValue: string}>(
    'campaign/collaboration/chatroom',
    async ({campaignId, collaborationId, force}, { getState, rejectWithValue }) => {
        try { 
            const STALE_TIME = apiConfig.FETCH_COLLABORATION_MILESTONE.staleTime;
            const state = getState() as {auth: AuthState, collaborations: CollaborationsSlice};
            const existingData = state.collaborations.chatRooms[campaignId]?.[collaborationId] ?? null
            if(!force && isFresh(existingData?.last_fetched_at ?? null, STALE_TIME)){
                return {campaignId, collaborationId, room: existingData.room, cache: true}
            }
            const room = await getCollabChatRoom(campaignId, collaborationId);
            return {campaignId, collaborationId, room, cache: false};
        } catch (error) {
            return rejectWithValue('Failed to fetch collaboration chatroom');
        }
    }
)